import {
  parsedOCRMetadataResponseSchema,
  type ParsedOCRMetadataResponse,
} from '@billing/validation/intakeItemDocument';
import {
  QueryClient,
  UseQueryResult,
  useQueries,
  useQuery,
} from '@tanstack/react-query';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';

import queryString from 'api/utils/queryStringBuilder';
import { getRequest as get, postRequest as post } from '../utils/HttpMethods';

import {
  convertFiltersToQueryString,
  convertGlobalFilterToQueryString,
  convertSortingToQueryString,
} from 'utils/filterUtils';

import {
  CountParams,
  CountReturnTypes,
  PaginatedResult,
  PaginationParams,
  StatusesCountResult,
  createEntity,
  updateEntity,
  useCountQuery,
  hasSiteID,
  useGetQuery,
  useMutationQuery,
  usePaginatedQuery,
  usePreFetchQuery,
} from 'api/utils/queries';

import {
  CreateIntakeItemInput,
  IntakeItem,
  intakeItemSchema,
  isIntakeItem,
} from '@billing/validation';
import {
  type ClaimConfidenceByIntakeItemID,
  type ClaimConfidenceInfo,
} from '@billing/validation/intakeItemDocument';
import { Claim } from 'interfaces/claim';

const isIntakeItemEntity = (obj: unknown): obj is IntakeItem =>
  isIntakeItem(obj);

export const intakeItemQueryKeys = {
  all: ['intakeItem'],
  get: (id: string) => [...intakeItemQueryKeys.all, 'get', id],
  getIntakeItemDocuments: (intakeItemIDs: string[]) => [
    ...intakeItemQueryKeys.all,
    'getIntakeItemDocuments',
    ...intakeItemIDs,
  ],
  parsedOCRMetadata: (documentID: string) => [
    ...intakeItemQueryKeys.all,
    'parsedOCRMetadata',
    documentID,
  ],
  siteStatusCount: (siteID: string) => [
    ...intakeItemQueryKeys.all,
    'siteStatusCount',
    siteID,
  ],
  providerStatusCount: (providerID: string, siteID: string | string[]) => [
    ...intakeItemQueryKeys.all,
    'providerStatusCount',
    providerID,
    siteID,
  ],
  userStatusCount: (userID: string, siteID: string | string[]) => [
    ...intakeItemQueryKeys.all,
    'userStatusCount',
    userID,
    siteID,
  ],
  paginatedBySite: (
    siteID: string,
    page: number,
    pageSize: number,
    filters: MRT_ColumnFiltersState,
    globalFilter: string,
    sorting: MRT_SortingState
  ) => [
    ...intakeItemQueryKeys.all,
    'site',
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
  ],
  paginatedByProvider: (
    providerID: string,
    siteID: string | string[],
    page: number,
    pageSize: number,
    filters: MRT_ColumnFiltersState,
    globalFilter: string,
    sorting: MRT_SortingState
  ) => [
    ...intakeItemQueryKeys.all,
    'provider',
    providerID,
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
  ],
  paginatedByUser: (
    userID: string,
    siteID: string | string[],
    page: number,
    pageSize: number,
    filters: MRT_ColumnFiltersState,
    globalFilter: string,
    sorting: MRT_SortingState
  ) => [
    ...intakeItemQueryKeys.all,
    'user',
    userID,
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
  ],
};

export const getIntakeItem = async (id: string): Promise<IntakeItem | null> =>
  await get<IntakeItem>(
    'QBilling',
    `/resource/intakeItem${queryString({ id })}`
  );

export const getIntakeItemDocuments = async ({
  intakeItemIDs,
}: {
  intakeItemIDs: string[];
}): Promise<ClaimConfidenceByIntakeItemID> => {
  if (!intakeItemIDs || intakeItemIDs.length === 0) {
    return {};
  }

  return post<ClaimConfidenceByIntakeItemID, { intakeItemIDs: string[] }>(
    'QBilling',
    '/resource/intakeItem/documents',
    {
      intakeItemIDs,
    }
  );
};

export const buildClaimConfidenceByClaimID = (
  lookupByIntakeItemID: ClaimConfidenceByIntakeItemID | undefined
) =>
  Object.fromEntries(
    Object.values(lookupByIntakeItemID ?? {}).flatMap((claimLookup) =>
      Object.entries(claimLookup).map(([claimID, meta]) => [
        claimID,
        meta.confidence,
      ])
    )
  ) as Record<string, number | null>;

export const buildClaimConfidenceInfoByClaimID = (
  lookupByIntakeItemID: ClaimConfidenceByIntakeItemID | undefined
) =>
  Object.fromEntries(
    Object.values(lookupByIntakeItemID ?? {}).flatMap((claimLookup) =>
      Object.entries(claimLookup)
    )
  ) as Record<string, ClaimConfidenceInfo>;

const getClaimConfidenceInfoByClaimID = async ({
  intakeItemIDs,
}: {
  intakeItemIDs: string[];
}): Promise<Record<string, ClaimConfidenceInfo>> =>
  buildClaimConfidenceInfoByClaimID(
    await getIntakeItemDocuments({ intakeItemIDs })
  );

export const useClaimConfidenceInfoByClaimID = ({
  claims,
}: {
  claims: Claim[];
}): UseQueryResult<Record<string, ClaimConfidenceInfo>, Error> => {
  const intakeItemIDs = Array.from(
    new Set(
      claims
        .map((claim) => claim.intakeItemID)
        .filter((id): id is string => Boolean(id))
    )
  ).sort();

  return useGetQuery<Record<string, ClaimConfidenceInfo>>({
    queryKey: intakeItemQueryKeys.getIntakeItemDocuments(intakeItemIDs),
    fetchFunction: getClaimConfidenceInfoByClaimID,
    isEntity: (data): data is Record<string, ClaimConfidenceInfo> =>
      Boolean(data) && typeof data === 'object',
    params: { intakeItemIDs },
    enabled: intakeItemIDs.length > 0,
  });
};

export const createIntakeItem = async (
  intakeItem: CreateIntakeItemInput
): Promise<IntakeItem | null> => {
  return createEntity<IntakeItem>({
    entity: intakeItem as IntakeItem,
    functionName: 'createIntakeItem',
    path: '/resource/intakeItem',
  });
};

const getSiteIntakeItemStatusesCount = async ({
  siteID,
}: CountParams): Promise<StatusesCountResult> => {
  return await get<StatusesCountResult>(
    'QBilling',
    `/resource/intakeItem/status/count${queryString({
      siteID,
    })}`
  );
};

const getProviderIntakeItemStatusesCount = async ({
  providerID,
  siteID,
}: CountParams): Promise<StatusesCountResult> => {
  return await get<StatusesCountResult>(
    'QBilling',
    `/resource/intakeItem/status/count${queryString({
      providerID,
      siteID,
    })}`
  );
};

/**
 * @description Fetches paginated intake items by site ID.
 * @param {Object} params - The parameters for the request.
 * @param {string} params.siteID - The ID of the site.
 * @param {number} params.page - The page number.
 * @param {number} params.pageSize - The number of items per page.
 * @param {MRT_ColumnFiltersState} params.filters - The column filters.
 * @param {string} params.globalFilter - The global filter.
 * @param {MRT_SortingState} params.sorting - The sorting state.
 * @returns {Promise<PaginatedResult<IntakeItem> | null>} The paginated intake items for the specified site.
 */
const paginatedIntakeItemsBySite = async ({
  siteID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
}: PaginationParams): Promise<PaginatedResult<IntakeItem> | null> => {
  const filterString = convertFiltersToQueryString(filters);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const sortingString = convertSortingToQueryString(sorting);
  return await get<PaginatedResult<IntakeItem>>(
    'QBilling',
    `/resource/intakeItem/site/page${queryString({
      siteID,
      page,
      pageSize,
      filters: filterString,
      globalFilter: globalFilterString,
      sorting: sortingString,
    })}`
  );
};

/**
 * @description Fetches paginated intake items by provider ID.
 * @param {Object} params - The parameters for the request.
 * @param {string} params.providerID - The ID of the provider.
 * @param {string} [params.siteID] - The ID of the site.
 * @param {number} params.page - The page number.
 * @param {number} params.pageSize - The number of items per page.
 * @param {MRT_ColumnFiltersState} params.filters - The column filters.
 * @param {string} params.globalFilter - The global filter.
 * @param {MRT_SortingState} params.sorting - The sorting state.
 * @returns {Promise<PaginatedResult<IntakeItem> | null>} The paginated intake items for the specified provider.
 */
const paginatedIntakeItemsByProvider = async ({
  providerID,
  siteID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
}: PaginationParams): Promise<PaginatedResult<IntakeItem> | null> => {
  const filterString = convertFiltersToQueryString(filters);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const sortingString = convertSortingToQueryString(sorting);
  return await get<PaginatedResult<IntakeItem>>(
    'QBilling',
    `/resource/intakeItem/provider/page${queryString({
      providerID,
      siteID,
      page,
      pageSize,
      filters: filterString,
      globalFilter: globalFilterString,
      sorting: sortingString,
    })}`
  );
};

const paginatedIntakeItemsByUser = async ({
  userID,
  siteID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
}: PaginationParams): Promise<PaginatedResult<IntakeItem> | null> => {
  const filterString = convertFiltersToQueryString(filters);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const sortingString = convertSortingToQueryString(sorting);
  return await get<PaginatedResult<IntakeItem>>(
    'QBilling',
    `/resource/intakeItem/user/page${queryString({
      userID,
      siteID,
      page,
      pageSize,
      filters: filterString,
      globalFilter: globalFilterString,
      sorting: sortingString,
    })}`
  );
};

/**
 * @description Hook to paginate intake items by site ID.
 * @param {Object} params - The parameters for the request.
 * @param {string} params.siteID - The ID of the site.
 * @param {number} [params.page=0] - The page number.
 * @param {number} [params.pageSize=10] - The number of items per page.
 * @param {MRT_ColumnFiltersState} [params.columnFilters=[]] - The column filters.
 * @param {string} [params.globalFilter=''] - The global filter.
 * @param {MRT_SortingState} [params.sorting=[]] - The sorting state.
 * @returns {UseQueryResult<PaginatedResult<IntakeItem>, Error>} The paginated intake items for the specified site.
 */
export const usePaginateIntakeItemsBySite = ({
  siteID,
  page = 0,
  pageSize = 10,
  columnFilters = [],
  globalFilter = '',
  sorting = [],
}: {
  siteID: string;
  page?: number;
  pageSize?: number;
  columnFilters?: MRT_ColumnFiltersState;
  globalFilter?: string;
  sorting?: MRT_SortingState;
}): UseQueryResult<PaginatedResult<IntakeItem>, Error> => {
  const enabled = !!siteID;

  return usePaginatedQuery<IntakeItem, PaginatedResult<IntakeItem>>({
    queryKey: intakeItemQueryKeys.paginatedBySite(
      siteID,
      page,
      pageSize,
      columnFilters,
      globalFilter,
      sorting
    ),
    isEntity: isIntakeItemEntity,
    functionName: 'paginateIntakeItemsBySite',
    params: {
      siteID,
      page,
      pageSize,
      filters: columnFilters,
      globalFilter,
      sorting,
    },
    fetchFunction: paginatedIntakeItemsBySite,
    enabled,
    validationSchema: intakeItemSchema,
  });
};

export const useGetIntakeItem = ({
  id,
}: {
  id: string;
}): UseQueryResult<IntakeItem | null, Error> => {
  return useGetQuery<IntakeItem>({
    queryKey: intakeItemQueryKeys.get(id),
    fetchFunction: () => getIntakeItem(id),
    isEntity: isIntakeItemEntity,
    functionName: 'getIntakeItem',
    params: { id },
    enabled: !!id,
    validationSchema: intakeItemSchema,
  });
};

export const useCreateIntakeItem = ({
  displayNotification = true,
}: {
  displayNotification?: boolean;
} = {}) => {
  return useMutationQuery<IntakeItem>({
    mutationFn: createIntakeItem,
    successMessage: 'Intake item created',
    errorMessage: 'Intake item creation failed',
    queryKey: intakeItemQueryKeys.all,
    displayNotification,
  });
};

export const useGetSiteIntakeItemStatusesCount = (
  siteID: string
): UseQueryResult<CountReturnTypes, Error> => {
  return useCountQuery({
    queryKey: intakeItemQueryKeys.siteStatusCount(siteID),
    functionName: 'getSiteIntakeItemStatusesCount',
    enabled: !!siteID,
    params: {
      siteID,
    },
    fetchFunction: getSiteIntakeItemStatusesCount,
  });
};

export const useGetProviderIntakeItemStatusesCount = ({
  providerID,
  siteID,
}: {
  providerID: string;
  siteID: string | string[];
}): UseQueryResult<CountReturnTypes, Error> => {
  return useCountQuery({
    queryKey: intakeItemQueryKeys.providerStatusCount(providerID, siteID),
    functionName: 'getProviderIntakeItemStatusesCount',
    enabled: !!providerID && hasSiteID(siteID),
    params: {
      providerID,
      siteID,
    },
    fetchFunction: getProviderIntakeItemStatusesCount,
  });
};

export const useGetUserIntakeItemStatusesCount = ({
  userID,
  siteID,
}: {
  userID: string;
  siteID: string | string[];
}): UseQueryResult<CountReturnTypes, Error> => {
  return useCountQuery({
    queryKey: intakeItemQueryKeys.userStatusCount(userID, siteID),
    functionName: 'getUserIntakeItemStatusesCount',
    enabled: !!userID && hasSiteID(siteID),
    params: { userID, siteID },
    fetchFunction: async ({
      userID: requestedUserID,
      siteID: requestedSiteID,
    }) =>
      await get<StatusesCountResult>(
        'QBilling',
        `/resource/intakeItem/status/count${queryString({
          userID: requestedUserID,
          siteID: requestedSiteID,
        })}`
      ),
  });
};

export const useGetIntakeItemsByIDs = ({
  ids,
}: {
  ids: string[];
}): {
  data: IntakeItem[];
  isLoading: boolean;
} => {
  const normalizedIDs = Array.from(
    new Set(ids.map((id) => id.trim()).filter(Boolean))
  );

  const results = useQueries({
    queries: normalizedIDs.map((id) => ({
      queryKey: intakeItemQueryKeys.get(id),
      queryFn: () => getIntakeItem(id),
      enabled: !!id,
      retry: false,
    })),
  });

  return {
    data: results
      .map((result) => result.data)
      .filter((item): item is IntakeItem =>
        Boolean(item && isIntakeItem(item))
      ),
    isLoading: results.some((result) => result.isLoading),
  };
};

/**
 * @description Hook to paginate intake items by provider ID.
 * @param {Object} params - The parameters for the request.
 * @param {string} params.providerID - The ID of the provider.
 * @param {string} [params.siteID] - The ID of the site.
 * @param {number} [params.page=0] - The page number.
 * @param {number} [params.pageSize=10] - The number of items per page.
 * @param {MRT_ColumnFiltersState} [params.columnFilters=[]] - The column filters.
 * @param {string} [params.globalFilter=''] - The global filter.
 * @param {MRT_SortingState} [params.sorting=[]] - The sorting state.
 * @returns {UseQueryResult<PaginatedResult<IntakeItem>, Error>} The paginated intake items for the specified provider.
 */
export const usePaginateIntakeItemsByProvider = ({
  providerID,
  userID,
  siteID,
  page = 0,
  pageSize = 10,
  columnFilters = [],
  globalFilter = '',
  sorting = [],
}: {
  providerID: string;
  userID?: string;
  siteID: string | string[];
  page?: number;
  pageSize?: number;
  columnFilters?: MRT_ColumnFiltersState;
  globalFilter?: string;
  sorting?: MRT_SortingState;
}): UseQueryResult<PaginatedResult<IntakeItem>, Error> => {
  const isUserScope = !!userID;
  const enabled = isUserScope
    ? hasSiteID(siteID)
    : !!providerID && hasSiteID(siteID);

  return usePaginatedQuery<IntakeItem, PaginatedResult<IntakeItem>>({
    queryKey: isUserScope
      ? intakeItemQueryKeys.paginatedByUser(
          userID,
          siteID,
          page,
          pageSize,
          columnFilters,
          globalFilter,
          sorting
        )
      : intakeItemQueryKeys.paginatedByProvider(
          providerID,
          siteID,
          page,
          pageSize,
          columnFilters,
          globalFilter,
          sorting
        ),
    isEntity: isIntakeItemEntity,
    functionName: isUserScope
      ? 'paginateIntakeItemsByUser'
      : 'paginateIntakeItemsByProvider',
    params: {
      providerID,
      userID,
      siteID,
      page,
      pageSize,
      filters: columnFilters,
      globalFilter,
      sorting,
    },
    fetchFunction: isUserScope
      ? paginatedIntakeItemsByUser
      : paginatedIntakeItemsByProvider,
    enabled,
    validationSchema: intakeItemSchema,
  });
};

/**
 * @description Hook to prefetch paginated intake items for a specific site.
 * @param queryClient - The query client instance.
 * @param siteID - The ID of the site.
 * @param page - The page number.
 * @param pageSize - The number of items per page.
 * @param filters - Optional filters to apply.
 * @param globalFilter - Optional global filter to apply.
 * @param sorting - Optional sorting parameters.
 */
export const usePreFetchPaginateIntakeItemsBySite = async (
  queryClient: QueryClient,
  siteID: string,
  page: number,
  pageSize: number,
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string,
  sorting?: MRT_SortingState
) => {
  const enabled = !!siteID;
  const functionName = 'paginateIntakeItemsBySite';

  const queryKey = intakeItemQueryKeys.paginatedBySite(
    siteID,
    page,
    pageSize,
    filters || [],
    globalFilter || '',
    sorting || []
  );

  await usePreFetchQuery({
    queryClient,
    queryKey: queryKey,
    isEntity: isIntakeItemEntity,
    functionName,
    params: {
      siteID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting,
    },
    fetchFunction: paginatedIntakeItemsBySite,
    enabled,
    displayNotification: false,
    validationSchema: intakeItemSchema,
  });
};

export const usePreFetchPaginateIntakeItemsByProvider = async (
  queryClient: QueryClient,
  providerID: string,
  siteID: string | undefined,
  page: number,
  pageSize: number,
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string,
  sorting?: MRT_SortingState
) => {
  const enabled = !!providerID && !!siteID;
  const functionName = 'paginateIntakeItemsByProvider';

  const queryKey = intakeItemQueryKeys.paginatedByProvider(
    providerID,
    siteID,
    page,
    pageSize,
    filters || [],
    globalFilter || '',
    sorting || []
  );

  await usePreFetchQuery({
    queryClient,
    queryKey,
    isEntity: isIntakeItemEntity,
    functionName,
    params: {
      providerID,
      siteID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting,
    },
    fetchFunction: paginatedIntakeItemsByProvider,
    enabled,
    displayNotification: false,
    validationSchema: intakeItemSchema,
  });
};

/**
 * @description Updates an intake item.
 * @param {Partial<IntakeItem> & { id: string }} intakeItem - The intake item data to update.
 * @returns {Promise<IntakeItem | null>} The updated intake item.
 */
const updateIntakeItem = async (
  intakeItem: Partial<IntakeItem> & { id: string }
): Promise<IntakeItem | null> => {
  return updateEntity<IntakeItem>({
    entity: intakeItem as IntakeItem,
    functionName: 'updateIntakeItem',
    isEntity: isIntakeItemEntity,
    path: '/resource/intakeItem',
  });
};

/**
 * @description Hook to update an intake item.
 * @returns {UseMutationResult<IntakeItem | null, Error>} The mutation result.
 */
export const useUpdateIntakeItem = () => {
  return useMutationQuery<IntakeItem>({
    mutationFn: updateIntakeItem,
    successMessage: 'Intake item updated',
    errorMessage: 'Intake item update failed',
    queryKey: intakeItemQueryKeys.all,
    displayNotification: true,
  });
};

export const getParsedOCRMetadata = async (
  documentID: string
): Promise<ParsedOCRMetadataResponse> =>
  parsedOCRMetadataResponseSchema.parse(
    await get<unknown>(
      'QBilling',
      `/resource/intakeItem/metadata${queryString({ documentID })}`
    )
  );

export const useParsedOCRMetadata = (
  documentID: string | undefined
): UseQueryResult<ParsedOCRMetadataResponse, Error> =>
  useQuery({
    queryKey: intakeItemQueryKeys.parsedOCRMetadata(documentID ?? ''),
    queryFn: () => getParsedOCRMetadata(documentID!),
    enabled: Boolean(documentID),
    retry: false,
    refetchOnMount: 'always',
    staleTime: 10 * 60 * 1000,
  });
